import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { map, uniq } from "lodash";

import {
  disableBodyScroll,
  enableBodyScroll,
} from "@root/misc/disableBodyScroll";

import { LineupOption, SlotKey, TeamID } from "../types";
import { Picker } from "../ui/pickerPopup/Picker";

import {
  PickerStateContext,
  stateReducer,
  usePickerActionDispatch,
} from "./pickerState";

interface IPickerContext {
  pick: (slot: SlotKey, options: LineupOption[]) => Promise<PickResult>;
  forceClose: () => void;
  openPromiseResolveRef: React.MutableRefObject<
    ((args: PickResult) => void) | undefined
  >;
}

export const PickerContext = React.createContext<IPickerContext>({
  pick: () => new Promise((res) => res({})),
  forceClose: () => {},
  openPromiseResolveRef: { current: undefined },
});

export const usePickerContext = () => useContext(PickerContext);

const PickerContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const dispatch = usePickerActionDispatch();

  const openPromiseResolveRef = useRef<(args: PickResult) => void>();

  const context: IPickerContext = useMemo(() => {
    return {
      pick: (slot, options) => {
        openPromiseResolveRef.current?.({ aborted: true });

        dispatch({
          type: "PICK_OPTION",
          payload: {
            slot,
            options,
            teams: uniq(map(options, (opt) => opt.team)),
          },
        });

        return new Promise((r) => {
          openPromiseResolveRef.current = r;
        });
      },
      forceClose: () => {
        openPromiseResolveRef.current?.({ aborted: true });
        dispatch({ type: "RESET_STATE" });
      },
      openPromiseResolveRef,
    };
  }, [dispatch]);

  return (
    <PickerContext.Provider value={context}>{children}</PickerContext.Provider>
  );
};

export interface PickResult {
  option?: LineupOption;
  aborted?: boolean;
  clearSlot?: boolean;
}

export interface ConfirmDialogInfo {
  newlyPickedOption?: LineupOption; // you can also just remove the old one!
  prevPickBoughtFor: number;
  prevPickPriceNow: number;
  reason:
    | "boughtForLessSoItsMaybeNotPossibleToReBuy"
    | "boughtForMoreAndWillReceiveTheInitialBuyingPrice";
}

export interface TeamFilter {
  teams: TeamID[];
  selected?: TeamID;
}

export const WithPicker: React.FC<PropsWithChildren<{}>> = React.memo(
  ({ children }) => {
    const [state, dispatch] = useReducer(stateReducer, {});

    useEffect(() => {
      if (state.openFor) {
        disableBodyScroll();
      } else {
        enableBodyScroll();
      }
    }, [state.openFor]);

    useEffect(() => {
      return () => {
        // make sure to enable body-scroll on unmount
        enableBodyScroll();
      };
    }, []);

    return (
      <PickerStateContext.Provider value={{ state, dispatch }}>
        <PickerContextProvider>
          <Picker />
          {children}
        </PickerContextProvider>
      </PickerStateContext.Provider>
    );
  }
);
